import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import TrialBanner from "@/components/trial/TrialBanner";
import ErrorBoundary from "@/components/error/ErrorBoundary";
import ErrorView from "@/components/error/ErrorView";
import { DEV_CRASH_SHELL_PATH, DevCrashShellProbe } from "@/components/error/devCrashProbes";
import { cn } from "@/lib/utils";
import { AssistantLip } from "@/agent/lip";
import { Menu } from "lucide-react";
import { MobileTopBarSlot } from "./MobileTopBar";

const DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 64;

const MainLayout: React.FC = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [topBarSlot, setTopBarSlot] = useState<HTMLDivElement | null>(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const activeDrawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <div
      className="min-h-screen"
      style={{ ["--drawer-width" as string]: `${activeDrawerWidth}px` }}
    >
      {import.meta.env.DEV && location.pathname === DEV_CRASH_SHELL_PATH ? (
        <DevCrashShellProbe />
      ) : null}

      <Sidebar
        drawerWidth={activeDrawerWidth}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        collapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed((prev) => !prev)}
      />

      {/* Mobile top bar — hamburger only, no header */}
      <div
        data-under-lip
        className="fixed left-0 right-0 top-[var(--env-ribbon-height)] z-20 flex h-12 items-center border-b border-indigo-100/60 bg-white/80 px-4 backdrop-blur-xl sm:hidden"
      >
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Open menu"
          className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-primary/5 hover:text-primary"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div ref={setTopBarSlot} className="ml-auto flex items-center gap-1.5" />
      </div>

      <main
        data-under-lip
        className={cn(
          "min-h-screen pb-[var(--assistant-lip-inset)] pt-12 sm:pt-0",
          "transition-[padding-left] duration-300 ease-spring",
          "sm:pl-[var(--drawer-width)]"
        )}
      >
        <TrialBanner />
        <ErrorBoundary key={location.pathname} fallback={<ErrorView />}>
          {/* key on the boundary (not Sidebar) remounts page content and clears a page throw */}
          <div className={cn("mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6", "animate-page-in")}>
            <MobileTopBarSlot.Provider value={topBarSlot}>
              <Outlet />
            </MobileTopBarSlot.Provider>
          </div>
        </ErrorBoundary>
      </main>

      {/* Outside the route-keyed boundary: navigating never remounts the assistant */}
      <AssistantLip />
    </div>
  );
};

export default MainLayout;
